import React, { useEffect, useState } from "react";
import { Box } from "../../../design-system";

export type HomeVariant = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H" | "I" | "J" | "K" | "L" | "M" | "N" | "O" | "P" | "Q" | "R" | "S" | "T" | "U" | "W" | "X" | "Y" | "Z";
const STORAGE_KEY = "cc_home_variant";
const COPY_KEY = "cc_home_copyset";
const VALID: HomeVariant[] = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "W", "X", "Y", "Z"];

/**
 * Home-page layout. R (the flagship page) is now what everyone lands on — it has the live
 * editor, upload, library access, the plugin section and pricing, which is the whole product
 * in one page. A–Q remain reachable via the toggle or ?layout= for comparison.
 *
 * This was A for months, which meant every stranger saw the oldest layout while all the work
 * went into R/S. The letters live in localStorage, so we only ever saw R because we'd set it.
 */
const DEFAULT_VARIANT: HomeVariant = "R";

/**
 * The A–U layout pills (and a remembered layout letter) are a lab tool — the list includes
 * the pitch deck — so they only show in a dev build or after visiting with ?lab=1 (which
 * remembers it in this browser; ?lab=0 turns it off). Everyone else gets the flagship page;
 * ?layout=X still opens one layout for a link you send.
 */
const LAB_KEY = "cc_home_lab";
export const isLayoutLabOn = (): boolean => {
  if (process.env.NODE_ENV !== "production") return true;
  try {
    const lab = new URLSearchParams(window.location.search).get("lab");
    if (lab === "1") localStorage.setItem(LAB_KEY, "1");
    if (lab === "0") localStorage.removeItem(LAB_KEY);
    return localStorage.getItem(LAB_KEY) === "1";
  } catch (e) {
    return false;
  }
};
export const useHomeVariant = (): [HomeVariant, (v: HomeVariant) => void] => {
  const [variant, setVariantState] = useState<HomeVariant>(DEFAULT_VARIANT);

  useEffect(() => {
    let initial: HomeVariant = DEFAULT_VARIANT;
    try {
      const params = new URLSearchParams(window.location.search);
      const q = (params.get("layout") || "").toUpperCase() as HomeVariant;
      if (VALID.includes(q)) {
        initial = q;
      } else if (isLayoutLabOn()) {
        const stored = (localStorage.getItem(STORAGE_KEY) || "").toUpperCase() as HomeVariant;
        if (VALID.includes(stored)) initial = stored;
      }
    } catch (e) {
      /* ignore */
    }
    setVariantState(initial);
  }, []);

  const setVariant = (v: HomeVariant) => {
    setVariantState(v);
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch (e) {
      /* ignore */
    }
  };

  return [variant, setVariant];
};

const LABELS: Record<HomeVariant, string> = {
  A: "Original",
  B: "Split",
  C: "Minimal",
  D: "Demo-right",
  E: "Two-CTA",
  F: "Cinematic",
  G: "Full page",
  H: "Glitch",
  I: "ET Lab",
  J: "Marketing",
  K: "Phones",
  L: "Pitch",
  M: "Studio",
  N: "Hero ET",
  O: "Split ET",
  P: "ET + Timeline",
  Q: "The One",
  R: "Flagship",
  S: "Plugin",
  T: "R + video",
  U: "Section lab",
  W: "App first",
  X: "Just type (old)",
  Y: "The sheet",
  Z: "The app",
};

/** Copy-set toggle (1·2·3) — swaps the hero headline/subhead on the full-page variant (G). */
export const useCopySet = (): [number, (n: number) => void] => {
  const [copySet, setCopyState] = useState<number>(0);
  useEffect(() => {
    try {
      const stored = parseInt(localStorage.getItem(COPY_KEY) || "0", 10);
      if (stored === 1 || stored === 2) setCopyState(stored);
    } catch (e) {
      /* ignore */
    }
  }, []);
  const setCopySet = (n: number) => {
    setCopyState(n);
    try {
      localStorage.setItem(COPY_KEY, String(n));
    } catch (e) {
      /* ignore */
    }
  };
  return [copySet, setCopySet];
};

export const CopyToggle: React.FC<{ copySet: number; setCopySet: (n: number) => void }> = ({ copySet, setCopySet }) => (
  <Box
    sx={{
      position: "fixed", bottom: 64, right: 16, zIndex: 12000,
      display: "flex", alignItems: "center", gap: "4px", padding: "5px 7px",
      borderRadius: "999px", backgroundColor: "rgba(18,17,24,0.9)", border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 6px 22px rgba(0,0,0,0.45)", backdropFilter: "blur(6px)", opacity: 0.55,
      transition: "opacity 0.15s", "&:hover": { opacity: 1 },
    }}
  >
    <Box component="span" sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#8d94a8", px: "4px", whiteSpace: "nowrap" }}>Copy</Box>
    {[0, 1, 2].map((n) => {
      const active = n === copySet;
      return (
        <Box key={n} component="button" onClick={() => setCopySet(n)}
          sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "12px", fontWeight: 600, color: active ? "#fff" : "#c2c7d6", backgroundColor: active ? "#e61d50" : "transparent", border: "none", borderRadius: "999px", padding: "5px 11px", lineHeight: 1 }}>
          {n + 1}
        </Box>
      );
    })}
  </Box>
);

/** Small, unobtrusive floating control to flip between layouts while experimenting. */
export const LayoutToggle: React.FC<{
  variant: HomeVariant;
  setVariant: (v: HomeVariant) => void;
  /** Which corner. "left" for pages that keep their own panel at the bottom-right (the Section Lab). */
  side?: "right" | "left";
}> = ({
  variant,
  setVariant,
  side = "right",
}) => !isLayoutLabOn() ? null : (
  <Box
    sx={{
      position: "fixed",
      bottom: 16,
      ...(side === "left" ? { left: 16 } : { right: 16 }),
      zIndex: 12000,
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "5px 7px",
      borderRadius: "999px",
      backgroundColor: "rgba(18,17,24,0.9)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 6px 22px rgba(0,0,0,0.45)",
      backdropFilter: "blur(6px)",
      opacity: 0.55,
      transition: "opacity 0.15s",
      "&:hover": { opacity: 1 },
    }}
  >
    <Box component="span" sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#8d94a8", px: "4px", whiteSpace: "nowrap" }}>
      Layout
    </Box>
    {VALID.map((v) => {
      const active = v === variant;
      return (
        <Box
          key={v}
          component="button"
          onClick={() => setVariant(v)}
          title={LABELS[v]}
          sx={{
            cursor: "pointer",
            fontFamily: "Outfit, sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            color: active ? "#fff" : "#c2c7d6",
            backgroundColor: active ? "#e61d50" : "transparent",
            border: "none",
            borderRadius: "999px",
            padding: "5px 11px",
            lineHeight: 1,
            transition: "background-color 0.15s",
          }}
        >
          {v}
        </Box>
      );
    })}
  </Box>
);
